import { type CSSProperties, useCallback, useEffect, useRef } from 'react';
import type { CharacterId } from '../lib/api';

/** Seconds of media to buffer ahead before starting or resuming playback. */
const BUFFER_CUSHION_S = 1.2;
/** Cap on the buffering hold, so replies shorter than the cushion still play. */
const MAX_BUFFER_WAIT_MS = 3000;

type PortraitProps = {
  characterId: CharacterId;
  imagePrefix: string;
  alt: string;
  awake: boolean;
  /** True while the sitter has stepped out of the frame. */
  away?: boolean;
  /** The empty painting shown while away. */
  awayPlateSrc?: string;
  videoSrc?: string | null;
  onVideoPlaying?: () => void;
  onVideoEnded?: () => void;
  onVideoError?: () => void;
};

type PortraitStyle = CSSProperties & {
  '--look-x': string;
  '--look-y': string;
};

export function Portrait({
  characterId,
  imagePrefix,
  alt,
  awake,
  away = false,
  awayPlateSrc,
  videoSrc = null,
  onVideoPlaying,
  onVideoEnded,
  onVideoError,
}: PortraitProps) {
  const root = useRef<HTMLDivElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  const onVideoErrorRef = useRef(onVideoError);
  onVideoErrorRef.current = onVideoError;

  useEffect(() => {
    const element = video.current;
    if (!videoSrc || !element) return;
    let poll = 0;

    const bufferedAhead = () => (element.buffered.length
      ? element.buffered.end(element.buffered.length - 1) - element.currentTime
      : 0);

    // The TTS/render chain delivers speech slightly slower than realtime, so
    // playback that starts on the first frame outruns the stream and stutters
    // every few seconds. Hold playback until a cushion is buffered (the wait
    // cap covers replies shorter than the cushion), and rebuffer the same way
    // after an underrun so a catch-up is one deliberate pause, not a stutter.
    const playWhenBuffered = () => {
      window.clearInterval(poll);
      // The wait cap starts once data exists: it releases replies shorter
      // than the cushion, and must not race the first bytes of the stream.
      let deadline = Number.POSITIVE_INFINITY;
      poll = window.setInterval(() => {
        const ahead = bufferedAhead();
        if (ahead > 0 && deadline === Number.POSITIVE_INFINITY) {
          deadline = performance.now() + MAX_BUFFER_WAIT_MS;
        }
        if (ahead < BUFFER_CUSHION_S && performance.now() < deadline) return;
        window.clearInterval(poll);
        element.play().catch((error: unknown) => {
          // Our own rebuffer pause() aborts a pending play(); that is not a
          // failure — the rebuffer loop calls play() again once cushioned.
          if ((error as Error)?.name !== 'AbortError') onVideoErrorRef.current?.();
        });
      }, 100);
    };
    const onWaiting = () => {
      element.pause();
      playWhenBuffered();
    };

    element.addEventListener('waiting', onWaiting);
    playWhenBuffered();
    return () => {
      window.clearInterval(poll);
      element.removeEventListener('waiting', onWaiting);
    };
  }, [videoSrc]);

  const onPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!root.current) return;
    const bounds = root.current.getBoundingClientRect();
    const x = ((event.clientX - bounds.left) / bounds.width - 0.5) * 2;
    const y = ((event.clientY - bounds.top) / bounds.height - 0.5) * 2;
    root.current.style.setProperty('--look-x', x.toFixed(3));
    root.current.style.setProperty('--look-y', y.toFixed(3));
  }, []);

  const onPointerLeave = useCallback(() => {
    root.current?.style.setProperty('--look-x', '0');
    root.current?.style.setProperty('--look-y', '0');
  }, []);

  const style: PortraitStyle = {
    '--look-x': '0',
    '--look-y': '0',
  };

  return (
    <div
      ref={root}
      className={`portrait portrait-${characterId} ${awake ? 'is-awake' : ''} ${away ? 'is-away' : ''}`}
      style={style}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
    >
      <div className="portrait-depth portrait-depth-back">
        <img src={`${imagePrefix}-neutral.png`} alt="" draggable="false" />
      </div>
      <div className="portrait-depth portrait-depth-body">
        <img src={`${imagePrefix}-neutral.png`} alt="" draggable="false" />
      </div>
      <div className="portrait-depth portrait-depth-face">
        <img src={`${imagePrefix}-neutral.png`} alt={alt} draggable="false" />
      </div>
      <div className="expression-layer blink-layer" aria-hidden="true">
        <img src={`${imagePrefix}-blink.png`} alt="" draggable="false" />
      </div>
      {awayPlateSrc && (
        <div className="away-plate" aria-hidden="true">
          <img src={awayPlateSrc} alt="" draggable="false" />
        </div>
      )}
      {videoSrc && (
        <div className="video-layer" aria-hidden="true">
          <video
            ref={video}
            src={videoSrc}
            playsInline
            onPlaying={onVideoPlaying}
            onEnded={onVideoEnded}
            onError={onVideoError}
          />
        </div>
      )}
      <div className="window-bloom" aria-hidden="true" />
      <div className="canvas-grain" aria-hidden="true" />
      <div className="portrait-vignette" aria-hidden="true" />
      <div className="dust dust-one" aria-hidden="true" />
      <div className="dust dust-two" aria-hidden="true" />
      <div className="dust dust-three" aria-hidden="true" />
    </div>
  );
}
